import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSocket } from "../contexts/SocketContext";
import {
  getNotificationsAction,
  markNotificationAsReadAction,
  markAllNotificationsAsReadAction,
  deleteNotificationAction,
  addNotificationAction,
  updateNotificationCountAction,
} from "../redux/actions/notificationActions";
import { FaBell } from "react-icons/fa";
import CommonLoading from "../components/loader/CommonLoading";
import NotificationReasonModal from "../components/modals/NotificationReasonModal";

const Notifications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { socket, isConnected } = useSocket();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [reasonModal, setReasonModal] = useState({ open: false, title: "", reason: "", message: "", targetPath: null });

  const notifications = useSelector((state) => state.user?.notifications || []);
  const unreadCount = useSelector((state) => state.user?.unreadNotificationCount || 0);

  // Fetch notifications
  const fetchNotifications = async (reset = false) => {
    const currentSkip = reset ? 0 : skip;
    setIsLoadingMore(!reset);
    
    const result = await dispatch(getNotificationsAction(20, currentSkip));
    
    if (result.success) {
      if (reset) {
        setSkip(20);
      } else {
        setSkip((prev) => prev + 20);
      }
      setHasMore(result.data.notifications.length === 20);
    }
    
    setIsLoading(false);
    setIsLoadingMore(false);
  };

  useEffect(() => {
    const init = async () => {
      await dispatch(markAllNotificationsAsReadAction());
      fetchNotifications(true);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on mount
  }, [dispatch]);

  // Listen for real-time notifications
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewNotification = (data) => {
      if (data.notification) {
        dispatch(addNotificationAction(data.notification));
        dispatch(updateNotificationCountAction(data.count || unreadCount + 1));
      }
    };

    const handleNotificationCount = (data) => {
      dispatch(updateNotificationCountAction(data.count || 0));
    };

    socket.on("new_notification", handleNewNotification);
    socket.on("notification_count", handleNotificationCount);

    return () => {
      socket.off("new_notification", handleNewNotification);
      socket.off("notification_count", handleNotificationCount);
    };
  }, [socket, isConnected, dispatch, unreadCount]);

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.isRead) {
      await dispatch(markNotificationAsReadAction(notification._id));
    }

    // Navigate based on notification type
    let targetPath = null;

    switch (notification.type) {
      case "follow_request_received":
        targetPath = "/skills-circle/received-follow-requests";
        break;
      case "profile_view_request_received":
        targetPath = "/skills-circle/received-profile-requests";
        break;
      case "follow_request_accepted":
        // Navigate to sender's profile
        if (notification.sender?.profileUrl) {
          targetPath = `/profile/${notification.sender.profileUrl}`;
        } else if (notification.sender?._id) {
          targetPath = `/profile/${notification.sender._id}`;
        }
        break;
      case "profile_view_request_accepted":
        // Navigate to sender's profile
        if (notification.sender?.profileUrl) {
          targetPath = `/profile/${notification.sender.profileUrl}`;
        } else if (notification.sender?._id) {
          targetPath = `/profile/${notification.sender._id}`;
        }
        break;
      case "post_liked":
      case "post_commented":
      case "comment_replied":
        if (notification.link) {
          targetPath = notification.link;
        } else if (notification.relatedEntityId) {
          targetPath = `/post/${notification.relatedEntityId}`;
        }
        break;
      case "post_reported":
      case "post_reinstated":
        if (notification.link) {
          targetPath = notification.link;
        } else if (notification.relatedEntityId) {
          targetPath = `/post/${notification.relatedEntityId}`;
        }
        setReasonModal({
          open: true,
          title: notification.title || "Notification",
          reason: notification.reason,
          message: notification.message,
          targetPath,
        });
        return;
      case "post_taken_down":
        targetPath = "/skills-feed";
        setReasonModal({
          open: true,
          title: notification.title || "Post Removed",
          reason: notification.reason,
          message: notification.message,
          targetPath,
        });
        return;
      default:
        if (notification.link) {
          targetPath = notification.link;
        }
    }

    if (targetPath) {
      navigate(targetPath);
    }
  };

  const handleReasonModalClose = () => {
    setReasonModal((prev) => ({ ...prev, open: false }));
  };

  const handleMarkAllAsRead = async () => {
    await dispatch(markAllNotificationsAsReadAction());
  };

  const handleDeleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    await dispatch(deleteNotificationAction(notificationId));
  };

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      fetchNotifications(false);
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInSeconds = Math.floor((now - notificationDate) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return notificationDate.toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CommonLoading />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-[52rem] mx-auto px-4 py-2">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-2">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-roboto-flex text-gray-900">Notifications</h1>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="px-3 py-1.5 text-sm font-medium text-primary hover:text-primary-700 border border-primary rounded-lg hover:bg-primary-50 transition-colors"
              >
                Mark all as read
              </button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {notifications.length === 0 ? (
            <div className="p-8 text-center">
              <FaBell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notification.isRead ? "bg-blue-50" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    {notification.sender?.avatar ? (
                      <img
                        src={notification.sender.avatar}
                        alt={notification.sender.name || "User"}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0 flex items-center justify-center">
                        <FaBell className="w-5 h-5 text-gray-400" />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-roboto-flex text-gray-900">
                            {notification.title}
                          </p>
                          <p className="text-sm font-roboto-flex text-gray-600 mt-0.5">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatTime(notification.createdAt)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-primary rounded-full" />
                          )}
                          <button
                            onClick={(e) =>
                              handleDeleteNotification(notification._id, e)
                            }
                            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                            aria-label="Delete notification"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Load More Button */}
          {hasMore && notifications.length > 0 && (
            <div className="p-4 border-t border-gray-200 text-center">
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="px-4 py-2 text-sm font-medium text-primary hover:text-primary-700 border border-primary rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoadingMore ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </div>
      </div>

      <NotificationReasonModal
        isOpen={reasonModal.open}
        onClose={handleReasonModalClose}
        title={reasonModal.title}
        reason={reasonModal.reason}
        message={reasonModal.message}
      />
    </div>
  );
};

export default Notifications;
